import { useTranslation } from 'react-i18next';

import { ICON_SIZE, ICON_STROKE, cn, iconRegistry } from '@/design-system';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { localeDefinition } from '@/shared/i18n';

/**
 * Sign-in illustration: the deal journey Customer → Documents → Deal → Bank → Approval → Settlement → Disbursal.
 * Decorative only (aria-hidden, nothing focusable). Drawn in tokens: graphite ink, one deep-green accent for progress,
 * positive green only on the final Disbursed step. Motion is SMIL on one small marker and seven opacity overlays
 * (no layout work); it is not rendered at all under prefers-reduced-motion or when `animated` is false.
 */

// Geometry on a 520 × 330 grid, left-to-right; mirrored for right-to-left languages.
const W = 520;
const ROW_1 = 84;
const ROW_2 = 234;
const NODE = 32;
/** One loop in seconds; the marker travels during the first 75% and the scene resets in the last 5%. */
const LOOP = 14;
const TRAVEL = 0.75;
const RESET = 0.95;

const STEPS = [
  { key: 'customer', glyph: iconRegistry.concept.customer.glyph, x: 70, y: ROW_1 },
  { key: 'documents', glyph: iconRegistry.journey.documents.glyph, x: 196, y: ROW_1 },
  { key: 'deal', glyph: iconRegistry.concept.deal.glyph, x: 322, y: ROW_1 },
  { key: 'bank', glyph: iconRegistry.concept.bank.glyph, x: 448, y: ROW_1 },
  { key: 'approval', glyph: iconRegistry.journey.approved.glyph, x: 448, y: ROW_2 },
  { key: 'settlement', glyph: iconRegistry.concept.settlement.glyph, x: 322, y: ROW_2 },
  { key: 'disbursal', glyph: iconRegistry.journey.disbursed.glyph, x: 196, y: ROW_2 },
] as const;

type StepKey = (typeof STEPS)[number]['key'];

/** Distance along the path to each step, and the whole length (straight segments only). */
const OFFSETS = STEPS.reduce<number[]>((acc, s, i) => {
  const prev = STEPS[i - 1];
  acc.push(prev ? (acc[i - 1] ?? 0) + Math.abs(s.x - prev.x) + Math.abs(s.y - prev.y) : 0);
  return acc;
}, []);
const LENGTH = OFFSETS[OFFSETS.length - 1] ?? 0;

/** SMIL key times with three decimals. */
const fmt = (n: number) => String(Math.round(n * 1000) / 1000);

export function JourneyVisual({ animated = true, className }: { animated?: boolean; className?: string }) {
  const { t, i18n } = useTranslation('auth');
  const rtl = localeDefinition(i18n.resolvedLanguage ?? i18n.language).dir === 'rtl';
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const moving = animated && !reducedMotion;

  const steps = STEPS.map((s) => ({ ...s, x: rtl ? W - s.x : s.x }));
  const path = steps.map((s, i) => `${i === 0 ? 'M' : 'L'}${s.x} ${s.y}`).join(' ');
  const iconOffset = (NODE - ICON_SIZE.sm) / 2;

  return (
    <svg viewBox={`0 0 ${W} 330`} aria-hidden="true" focusable="false" className={cn('h-auto w-full', className)}>
      {/* The journey, and the progress drawn over it in the accent. */}
      <path d={path} fill="none" className="stroke-border-strong" strokeWidth={1.5} />
      <path
        d={path}
        fill="none"
        className="stroke-accent"
        strokeWidth={1.5}
        strokeDasharray={LENGTH}
        strokeDashoffset={moving ? LENGTH : 0}
      >
        {moving ? (
          <>
            <animate attributeName="stroke-dashoffset" values={`${LENGTH};0;0`} keyTimes={`0;${TRAVEL};1`} dur={`${LOOP}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="1;1;0" keyTimes={`0;${RESET};1`} dur={`${LOOP}s`} repeatCount="indefinite" />
          </>
        ) : null}
      </path>

      {/* A document travelling along the path; drawn under the steps so it passes into each one. */}
      {moving ? (
        <g>
          <rect x={-5} y={-6.5} width={10} height={13} rx={1.5} className="fill-surface stroke-ink" strokeWidth={1.25} />
          <path d="M-2.5 -2h5M-2.5 1h5M-2.5 4h3" className="stroke-ink-muted" strokeWidth={1} />
          <animateMotion path={path} keyPoints="0;1;1" keyTimes={`0;${TRAVEL};1`} calcMode="linear" dur={`${LOOP}s`} repeatCount="indefinite" />
        </g>
      ) : null}

      {steps.map((s, i) => {
        const Glyph = s.glyph;
        const final = i === steps.length - 1;
        const at = (TRAVEL * (OFFSETS[i] ?? 0)) / LENGTH;
        const on = Math.min(at + 0.025, RESET);
        const x0 = s.x - NODE / 2;
        const y0 = s.y - NODE / 2;
        return (
          <g key={s.key}>
            {/* Waiting step: white square, muted glyph. */}
            <rect x={x0} y={y0} width={NODE} height={NODE} rx={4} className="fill-surface stroke-border-strong" strokeWidth={1} />
            <g transform={`translate(${x0 + iconOffset} ${y0 + iconOffset})`} className="text-ink-muted">
              <Glyph size={ICON_SIZE.sm} strokeWidth={ICON_STROKE.default} />
            </g>
            {/* Reached step: accent (Disbursed: positive), fading in as the document arrives. */}
            <g opacity={moving ? 0 : 1}>
              <rect
                x={x0}
                y={y0}
                width={NODE}
                height={NODE}
                rx={4}
                className={final ? 'fill-positive-subtle stroke-positive' : 'fill-accent-subtle stroke-accent'}
                strokeWidth={1.25}
              />
              <g transform={`translate(${x0 + iconOffset} ${y0 + iconOffset})`} className={final ? 'text-positive' : 'text-accent'}>
                <Glyph size={ICON_SIZE.sm} strokeWidth={ICON_STROKE.default} />
              </g>
              {moving ? (
                <animate
                  attributeName="opacity"
                  values="0;0;1;1;0"
                  keyTimes={`0;${fmt(at)};${fmt(on)};${RESET};1`}
                  dur={`${LOOP}s`}
                  repeatCount="indefinite"
                />
              ) : null}
            </g>
            <text x={s.x} y={s.y + NODE / 2 + 20} textAnchor="middle" className="fill-ink-secondary text-meta">
              {t(`journey.${s.key satisfies StepKey}`)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
